// ─── Accueil A/B (agent v2 `first_reply`, 2026-10-30) ──────────────────────
//
// Owner spec (docs/agent-v2/SPEC.md, PHASE B PART 2): the vague first message
// gets the LONG or the SHORT welcome, 50/50 per chat, for two weeks. « Measure:
// reply rate, photos sent, advance paid, Duo/Trio share. »
//
// Read-only. A chat counts once its welcome actually left (worker
// lib/firstReplyRun.ts `abStats`); an arm drawn for a draft that never went
// out is not in the test.
import { useQuery } from '@tanstack/react-query';
import { whatsappApi, type WelcomeArmStats } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';

const MODE_LABEL: Record<string, string> = {
  off: 'Coupé — premier message comme avant',
  test: 'Test — seulement sur les numéros de test',
  on: 'Actif pour tous les clients',
};

const ARM_LABEL: Record<WelcomeArmStats['variant'], string> = { long: 'Accueil long', short: 'Accueil court' };

/** « 12 (40 %) » — the count first, the share of the arm's chats beside it. */
export function share(n: number, of: number): string {
  if (!of) return `${n}`;
  return `${n} (${Math.round((n / of) * 100)} %)`;
}

export function WelcomeAbCard() {
  const { t } = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-v2-welcome-ab'],
    queryFn: () => whatsappApi.agentWelcomeAb(),
    refetchInterval: 300_000,
  });
  const arms = data?.arms ?? [];
  const total = arms.reduce((n, a) => n + a.chats, 0);
  const mode = data?.mode ?? 'off';

  return (
    <SettingGroup
      title={t('Accueil A/B (premier message)')}
      summary={isLoading ? t('Chargement…') : `${total} ${t('conversations sur 30 jours')} · ${t(MODE_LABEL[mode] ?? MODE_LABEL.off)}`}
      tone="default"
      defaultOpen={false}
      loading={isLoading}
    >
      <p className="text-[11.5px] text-gray-500 mt-3 leading-snug max-w-2xl">
        {t(
          'Quand le premier message d’un client est vague (« Salam », « prix ? »), il reçoit l’accueil long ou l’accueil court, '
          + 'moitié-moitié, toujours le même pour une conversation. Chaque ligne compte les conversations après l’accueil.',
        )}
      </p>
      {data?.column_missing && (
        <p className="text-[11.5px] text-amber-700 mt-2">{t('Colonnes pas encore créées (migration 276).')}</p>
      )}
      {isError && <p className="text-[11.5px] text-red-600 mt-3" role="alert">{t('Impossible de charger le test.')}</p>}

      {!isLoading && !isError && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[12px] tabular-nums">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1.5 pr-3 font-medium">{t('Version')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('Conversations')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('Ont répondu')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('Ont envoyé des photos')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('Avance payée')}</th>
                <th className="py-1.5 font-medium">{t('Duo / Trio (des commandes)')}</th>
              </tr>
            </thead>
            <tbody>
              {arms.map((a) => (
                <tr key={a.variant} className="border-t border-gray-100 text-gray-800">
                  <td className="py-1.5 pr-3 font-semibold">{t(ARM_LABEL[a.variant])}</td>
                  <td className="py-1.5 pr-3">{a.chats}</td>
                  <td className="py-1.5 pr-3">{share(a.replied, a.chats)}</td>
                  <td className="py-1.5 pr-3">{share(a.sent_photos, a.chats)}</td>
                  <td className="py-1.5 pr-3">{share(a.advance_paid, a.chats)}</td>
                  <td className="py-1.5">{share(a.duo_trio, a.orders)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SettingGroup>
  );
}
